// MARK://dhk8/pos/lib/clients/catalog
// Typed client for dhk8-catalog-menus — reconciled against the published contract
// at services/dhk8-catalog-menus/src/contract.ts (commit f65c5b6).
//
// ROUTE CONVENTION: the service currently exposes `/api/menus...` (deployed state);
// the canonical DHK8 convention is `/v1/...`. Mirroring the backend's TODO(reconcile):
// when the ingress + MENU_ROUTES migrate to /v1/, switch `MENU_BASE` here too.
//
// RESPONSE ENVELOPE: { ok: true, data: T } | { ok: false, error: { code, message } }.
//
// NOTE / TODO(backend): dhk8-catalog-menus only knows menu *structure* — a
// MenuProductDto is a placement (sectionId, productId, ordinal, visible,
// overridePriceCents), not the product itself (name, sku, base price live in the
// products/pricing clusters). `flattenMenuForGrid` below is therefore best-effort
// until a "resolved menu" endpoint (menus ⨝ products ⨝ pricing) exists.

import type { ProductWithStock, CategoryRow } from '@dockethub/ui-restaurant'
import { apiOrigin } from '../api-origin.js'
import { apiFetch, getTenantId, svcUrl } from '../api.js'
import { gqlClient } from '../gql.js'
import { PosActiveMenuQuery, PosMenuScreenQuery, PosSetProductAvailabilityMutation } from '../queries/catalog.queries.js'
import {
  PREVIEW_STUB,
  SEED_MENU,
  previewProductExtras,
  productDisplayMeta,
  type PreviewProductExtras,
  type ProductDisplayMeta,
} from '../seed-preview.js'

// ── Mirror of services/dhk8-catalog-menus/src/contract.ts — keep in sync ────────

export interface MenuProductDto {
  id: string
  sectionId: string
  productId: string
  ordinal: number
  visible: boolean
  overridePriceCents: number | null
}

export interface MenuSectionDto {
  id: string
  menuId: string
  name: string
  ordinal: number
  active: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  products: MenuProductDto[]
}

export interface MenuDto {
  id: string
  tenantId: string
  branchId: string | null
  name: string
  daysOfWeek: number[]
  startTime: string | null
  endTime: string | null
  active: boolean
  publishedAt: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  sections: MenuSectionDto[]
}

export interface MenuListResponse {
  items: MenuDto[]
  total: number
}

export interface MenuListQuery {
  tenantId: string
  branchId?: string
  page?: number
  limit?: number
}

// ── envelope unwrap ─────────────────────────────────────────────────────────────

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }

async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  // Some endpoints may not be enveloped yet — pass through.
  return body as T
}

// ── routes ──────────────────────────────────────────────────────────────────────

const MENU_BASE = `${apiOrigin()}/api/menus` // TODO(reconcile): → `${apiBase()}/catalog-menus/menus`

/** GET /api/menus — paged list of menus (each includes its sections + product placements). */
export async function listMenus(query: Partial<MenuListQuery> = {}, signal?: AbortSignal): Promise<MenuListResponse> {
  const tenantId = query.tenantId ?? getTenantId()
  const params = new URLSearchParams()
  if (tenantId) params.set('tenantId', tenantId)
  if (query.branchId) params.set('branchId', query.branchId)
  if (query.page) params.set('page', String(query.page))
  if (query.limit) params.set('limit', String(query.limit))
  const qs = params.toString()
  return fetchData<MenuListResponse>(`${MENU_BASE}${qs ? `?${qs}` : ''}`, { signal })
}

/** GET /api/menus/:id — a single menu with sections + product placements. */
export async function getMenu(id: string, signal?: AbortSignal): Promise<MenuDto> {
  return fetchData<MenuDto>(`${MENU_BASE}/${encodeURIComponent(id)}`, { signal })
}

// ── adapters for the @dockethub/ui-restaurant MenuGrid (best-effort — see header note) ─

type StockStatus = 'OK' | 'LOW' | 'OUT' | 'UNTRACKED'

export interface FlatMenu {
  products: ProductWithStock[]
  categories: CategoryRow[]
}

/** Flatten a MenuDto into the shape <MenuGrid> consumes. Sections → categories,
 *  product placements → products. Product name/sku/base price are NOT resolved here
 *  (they live in other clusters) — `name`/`sku` fall back to the placement productId
 *  and `unitPriceCents` to the override; replace once a resolved-menu endpoint exists. */
export function flattenMenuForGrid(menu: MenuDto): FlatMenu {
  const liveSections = menu.sections.filter((s) => s.active && !s.deletedAt)

  const categories: CategoryRow[] = liveSections.map((s) => ({ id: s.id, name: s.name, sortOrder: s.ordinal }))

  const products: ProductWithStock[] = liveSections.flatMap((s) =>
    s.products
      .filter((p) => p.visible)
      .slice()
      .sort((a, b) => a.ordinal - b.ordinal)
      .map<ProductWithStock>((p) => ({
        id: p.productId, // the real product id (placement id is p.id) — what "add to order" needs
        name: p.productId, // TODO(backend): resolve from products cluster
        sku: p.productId,
        unitPriceCents: p.overridePriceCents ?? 0, // TODO(backend): resolve from pricing cluster
        // 2026-05-25 — Lane A IVA: catálogo MX precios al público (IVA incluido).
        // Pre-Lane-A enviábamos '0' aquí, lo cual bypaseaba el cálculo backend
        // (splitLineTax con rate=0 retorna tax=0 inmediato). Default 16% para
        // que el desglose contable funcione hasta que la TaxProfile real
        // del producto llegue desde dhk8-catalog-menus.
        // TODO(backend): leer el taxRate real del Product/TaxProfile en lugar
        // de hardcodear aquí (cuando admin UI exponga el toggle).
        taxRate: '0.16',
        isAvailable: true,
        categoryId: s.id,
        stockStatus: 'UNTRACKED',
      })),
  )

  return { products, categories }
}

// ── Active menu (POS catalog source-of-truth) ───────────────────────────────────
// Mirrors services/dhk8-catalog-menus/src/api/menus-active.ts. The endpoint
// projects dhk8_erp products into menu sections (alphabetic by category, items
// alphabetic by name). Stop-gap until a real Menu entity lands in dhk8_catalog.

export interface ActiveMenuItemDto {
  id: string
  name: string
  priceCents: number
  currency: string
  imageUrl: string | null
  unit: string | null
  sku: string
  description: string | null
}

export interface ActiveMenuSectionDto {
  id: string
  name: string
  sortOrder: number
  items: ActiveMenuItemDto[]
}

export interface ActiveMenuDto {
  id: string
  name: string
  sections: ActiveMenuSectionDto[]
}

/** Adapter: ActiveMenuDto → FlatMenu (the shape <MenuGrid> consumes). Sections
 *  become categories; items become products with categoryId pointing at the
 *  owning section. Empty sections are already filtered server-side. */
export function activeMenuToFlat(menu: ActiveMenuDto): FlatMenu {
  const categories: CategoryRow[] = menu.sections.map((s) => ({ id: s.id, name: s.name, sortOrder: s.sortOrder }))
  const products: ProductWithStock[] = menu.sections.flatMap((s) =>
    s.items.map<ProductWithStock>((it) => ({
      id: it.id,
      name: it.name,
      sku: it.sku,
      ...(it.description ? { description: it.description } : {}),
      unitPriceCents: it.priceCents,
      // 2026-05-25 — Lane A IVA (ver nota arriba). Default 16% MX.
      taxRate: '0.16',
      isAvailable: true,
      categoryId: s.id,
      ...(it.imageUrl ? { imageUrl: it.imageUrl } : {}),
      stockStatus: 'UNTRACKED',
    })),
  )
  return { products, categories }
}

/** Convenience: fetch the active menu for this tenant, flattened for the grid.
 *  Calls dhk8-catalog-menus `/v1/menus/active`, which projects ERP products. */
interface GraphMenuProduct { productId: string; ordinal: number; visible: boolean; overridePriceCents: number | null; product: { id: string; name: string; sku: string; priceCents: number; description: string | null; imageUrl: string | null; availability?: { canMake: number | null; tracked: boolean } | null } | null }
interface GraphMenuSection { id: string; name: string; ordinal: number; active: boolean; products: GraphMenuProduct[] }
interface GraphMenu { id: string; name: string; active: boolean; sections: GraphMenuSection[] }

/** Adapter: menú del grafo (catalog-menus + producto federado de erp-products) → FlatMenu.
 *  Espejo de activeMenuToFlat pero sobre la forma normalizada del grafo. Las secciones
 *  son categorías; cada MenuProduct se aplana con el producto resuelto por federación
 *  (overridePriceCents gana sobre el precio base). Productos no resueltos se omiten. */
function graphMenuToFlat(menu: GraphMenu): FlatMenu {
  const sections = [...menu.sections].sort((a, b) => a.ordinal - b.ordinal)
  const categories: CategoryRow[] = sections.map((s) => ({ id: s.id, name: s.name, sortOrder: s.ordinal }))
  const products: ProductWithStock[] = sections.flatMap((s) =>
    [...s.products]
      .sort((a, b) => a.ordinal - b.ordinal)
      .filter((mp) => mp.product !== null && mp.visible)
      .map<ProductWithStock>((mp) => {
        const prod = mp.product as NonNullable<GraphMenuProduct['product']>
        return {
          id: prod.id,
          name: prod.name,
          sku: prod.sku,
          ...(prod.description ? { description: prod.description } : {}),
          unitPriceCents: mp.overridePriceCents ?? prod.priceCents,
          // 2026-05-25 — Lane A IVA (ver nota arriba). Default 16% MX.
          taxRate: '0.16',
          isAvailable: true,
          categoryId: s.id,
          ...(prod.imageUrl ? { imageUrl: prod.imageUrl } : {}),
          // F9.3 / ADR-0008: la disponibilidad llega en la MISMA query de
          // composición (product.availability, federado desde erp-inventory) →
          // se acabó el enrichWithStock por REST. tracked:false → UNTRACKED;
          // canMake 0 → OUT; canMake>0 → OK (availablePortions = canMake).
          ...availabilityToStock(prod.availability),
        }
      }),
  )
  return { products, categories }
}

/** F9.3: mapea Product.availability (canMake/tracked, federado de erp-inventory)
 *  al shape de badge del MenuGrid. Sin base de stock → UNTRACKED. */
function availabilityToStock(av: { canMake: number | null; tracked: boolean } | null | undefined): { stockStatus: StockStatus; availablePortions?: number | null } {
  if (!av || !av.tracked) return { stockStatus: 'UNTRACKED' }
  if (av.canMake === null) return { stockStatus: 'UNTRACKED' }
  if (av.canMake <= 0) return { stockStatus: 'OUT', availablePortions: 0 }
  return { stockStatus: 'OK', availablePortions: av.canMake }
}

export async function getActiveMenuFlat(signal?: AbortSignal): Promise<FlatMenu> {
  if (PREVIEW_STUB) return structuredClone(SEED_MENU)
  // F9.3 / ADR-0008: el menú activo se sirve por la composición de la vertical
  // (nintaigo-graph → `menuScreen`). Primero descubrimos el id del menú activo
  // (query liviana `menus`, mismo contrato que Admin); luego UNA sola
  // `menuScreen(id)` arma menú→secciones→producto(imagen/precio)+availability
  // (inventario) por federación — reemplaza el par PosActiveMenu + enrichWithStock.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const list = await gqlClient.query(PosActiveMenuQuery, { limit: 50 }, ctx).toPromise()
  if (list.error) throw list.error
  const menus = (list.data?.menus?.items ?? []) as unknown as Array<{ id: string; active: boolean }>
  const activeId = (menus.find((m) => m.active) ?? menus[0])?.id
  if (!activeId) return { products: [], categories: [] }
  return getMenuScreenFlat(activeId, signal)
}

/** F9.3 / ADR-0008: pantalla de menú servida por UNA query de composición de la
 *  vertical (`menuScreen`). Imagen + dato + inventario en la misma respuesta. */
export async function getMenuScreenFlat(id: string, signal?: AbortSignal): Promise<FlatMenu> {
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosMenuScreenQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const menu = res.data?.menuScreen as unknown as GraphMenu | null | undefined
  if (!menu) return { products: [], categories: [] }
  return graphMenuToFlat(menu)
}

// ── product detail (store-style /menu dialog) ───────────────────────────────────
// A `ProductDetail` is a grid product enriched with the presentational display
// fields (photo / description / trend / "más vendido") and its variation/option
// groups. In a real build the modifier groups would come from dhk8-catalog-modifiers
// and the display fields from dhk8-catalog-products; for now they're layered from the
// preview seed (and are simply absent in non-preview builds — the dialog degrades).

export interface ProductDetail extends ProductWithStock {
  display: ProductDisplayMeta
  extras: PreviewProductExtras
}

/** Synchronously enrich a product already in hand (e.g. from the menu grid) with its
 *  display metadata + variation/option groups. Safe to call in any build — outside the
 *  preview the extra maps are empty so it just echoes the product through. */
export function buildProductDetail(product: ProductWithStock): ProductDetail {
  const seedDisplay = productDisplayMeta(product.id)
  // Real products (no preview seed) traen imageUrl en el grid pero seedDisplay
  // viene vacío → el modal mostraba el placeholder. Caemos al product.imageUrl
  // cuando el seed no tiene foto propia.
  const display: ProductDisplayMeta = {
    ...seedDisplay,
    ...(seedDisplay.imageUrl ? {} : product.imageUrl ? { imageUrl: product.imageUrl } : {}),
  }
  const extras = previewProductExtras(product.id)
  return { ...product, display, extras }
}

/** Fetch the active menu and return the enriched detail for one product id, or null
 *  if it isn't in the active menu. (The dialog usually has the product already and
 *  uses `buildProductDetail`; this is the lookup-by-id seam for deep links.) */
export async function getProductDetail(productId: string, signal?: AbortSignal): Promise<ProductDetail | null> {
  const flat = await getActiveMenuFlat(signal)
  const product = flat.products.find((p) => p.id === productId)
  return product ? buildProductDetail(product) : null
}

// ── 86 / agotar producto ─────────────────────────────────────────────────────
// Toggle de disponibilidad de un producto en servicio (el "86" de cocina). La
// mutación `setProductAvailability` (erp-products, tenant del JWT) togglea
// `Product.active`; el caller invalida el menú activo para refrescar el badge
// "Agotado". ESCRITURA por el grafo (mismo patrón que las demás mutations).
export interface ProductAvailabilityResult {
  id: string
  active: boolean
}

/** Marca un producto como disponible (`available: true`) o agotado/86
 *  (`available: false`) en el tenant del JWT. Devuelve el id + el `active`
 *  resultante. Bajo el preview stub no hay backend → eco optimista. */
export async function setProductAvailability(productId: string, available: boolean): Promise<ProductAvailabilityResult> {
  if (PREVIEW_STUB) {
    // El stub del menú no persiste `active`; devolvemos el estado pedido para que
    // la UI optimista del caller siga funcionando sin backend.
    return { id: productId, active: available }
  }
  const res = await gqlClient.mutation(PosSetProductAvailabilityMutation, { id: productId, available }).toPromise()
  if (res.error) throw res.error
  const p = res.data?.setProductAvailability
  if (!p) throw new Error('SET_PRODUCT_AVAILABILITY_FAILED')
  return { id: p.id, active: p.active }
}
